import { useState, useCallback } from 'react';
import { QueryType } from '@sendbird/chat/groupChannel';
import type {
  GroupChannelListQueryParams,
  GroupChannelCreateParams,
  GroupChannel,
  GroupChannelListQuery,
} from '@sendbird/chat/groupChannel';
import SentryService from '@/services/sentry/Sentry.service';

export interface GroupChannelNamespace {
  createMyGroupChannelListQuery(
    params: GroupChannelListQueryParams,
  ): GroupChannelListQuery;

  createDistinctChannelIfNotExist(
    params: GroupChannelCreateParams,
  ): Promise<GroupChannel>;
}

export const useSendbirdChannelUrlCore = (
  groupChannel: GroupChannelNamespace,
  isInitialized: boolean,
) => {
  const [channelUrl, setChannelUrl] = useState<string>();
  const [error, setError] = useState<unknown>();

  const fetchChannelUrl = useCallback(
    async (stylistId: number, clientId: number) => {
      if (!isInitialized) {
        throw new Error('Sendbird SDK not ready');
      }

      setError(undefined);

      try {
        const listQueryParams: GroupChannelListQueryParams = {
          userIdsFilter: {
            queryType: QueryType.AND,
            userIds: [String(stylistId), String(clientId)],
            includeMode: false,
          },
          includeEmpty: true,
          limit: 1,
        };

        const listQuery =
          groupChannel.createMyGroupChannelListQuery(listQueryParams);

        const channels = await listQuery.next();

        let targetChannel: GroupChannel;
        if (channels.length > 0) {
          targetChannel = channels[0];
        } else {
          const createParams: GroupChannelCreateParams = {
            invitedUserIds: [String(stylistId), String(clientId)],
            operatorUserIds: [String(stylistId)],
            isDistinct: true,
          };
          targetChannel =
            await groupChannel.createDistinctChannelIfNotExist(createParams);
        }

        setChannelUrl(targetChannel.url);
        return targetChannel.url;
      } catch (error) {
        setError(error);
        SentryService.captureError(
          `Error fetching Sendbird channel URL for stylist ${stylistId} and client ${clientId}`,
          error,
        );
      }
    },
    [groupChannel, isInitialized],
  );

  return { fetchChannelUrl, channelUrl, error };
};
